/**
 * Analytics Event Schemas (Zod)
 *
 * Runtime validation schemas for all analytics events.
 * Ensures data quality and consistency before events are sent to Mixpanel.
 *
 * @see https://zod.dev/
 * @version 1.0.0 - December 2025
 */

import { z } from 'zod';
import { logger } from '@/shared/lib/logger/logger';
import {
  DROP_OFF_EVENTS,
  type DropOffEventName,
  ENGAGEMENT_EVENTS,
  type EngagementEventName,
  FUNNEL_EVENTS,
  type FunnelEventName,
} from './events';

// =============================================================================
// BASE SCHEMAS
// =============================================================================

/**
 * Schema for URL that also accepts empty string (e.g., document.referrer can be "").
 * Empty strings are transformed to undefined before URL validation.
 */
const optionalUrlSchema = z
  .union([
    z.literal(''), // Allow empty string
    z.string().url(), // Or valid URL
  ])
  .optional();

/**
 * Base event properties schema (enriched automatically).
 * These are added by useAnalytics hook, not by callers.
 */
const BaseEventPropertiesSchema = z.object({
  timestamp: z.string().datetime().optional(),
  page_url: z.string().url().optional(),
  session_id: z.string().nullable().optional(),
  device_type: z.enum(['mobile', 'tablet', 'desktop']).optional(),
  locale: z.string().optional(),
  referrer: optionalUrlSchema,
  event_category: z.enum(['funnel', 'engagement', 'drop_off']).optional(),
});

// =============================================================================
// FUNNEL EVENT SCHEMAS
// =============================================================================

const LandingViewedPropertiesSchema = BaseEventPropertiesSchema.extend({
  referrer: optionalUrlSchema,
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
});

const WorkshopStartedPropertiesSchema = BaseEventPropertiesSchema.extend({
  has_existing_session: z.boolean(),
  referrer: optionalUrlSchema,
  entry_point: z.string().optional(),
});

const InputMethodSelectedPropertiesSchema = BaseEventPropertiesSchema.extend({
  input_method: z.enum(['photo', 'prompt', 'wizard', 'selections', 'ai_assistant']),
  time_to_select_ms: z.number().int().positive().optional(),
});

const GenerationStartedPropertiesSchema = BaseEventPropertiesSchema.extend({
  input_method: z.enum(['photo', 'prompt', 'wizard', 'selections', 'ai_assistant']),
  has_photo: z.boolean(),
  prompt_length: z.number().int().nonnegative().optional(),
  photo_asset_id: z.string().uuid().optional(),
});

const GenerationCompletedPropertiesSchema = BaseEventPropertiesSchema.extend({
  variant_count: z.number().int().positive(),
  generation_time_ms: z.number().int().nonnegative(),
  success: z.boolean(),
  session_id: z.string().optional(),
  input_method: z.string().optional(),
  subject_type: z.string().optional(),
});

const GenerationFailedPropertiesSchema = BaseEventPropertiesSchema.extend({
  error_message: z.string().min(1),
  error_code: z.string().optional(),
  input_method: z.string().optional(),
  failure_stage: z.string().optional(),
});

const VariantSelectedPropertiesSchema = BaseEventPropertiesSchema.extend({
  variant_index: z.number().int().nonnegative(),
  variant_id: z.string().uuid().optional(),
  time_to_select_ms: z.number().int().positive().optional(),
  total_variants: z.number().int().positive().optional(),
});

const ProductConfiguredPropertiesSchema = BaseEventPropertiesSchema.extend({
  product_type: z.string().min(1),
  size: z.string().optional(),
  color: z.string().optional(),
  price: z.number().nonnegative(),
  currency: z.string().length(3),
  product_id: z.string().uuid().optional(),
});

const CartViewedPropertiesSchema = BaseEventPropertiesSchema.extend({
  item_count: z.number().int().nonnegative(),
  total_value: z.number().nonnegative(),
  currency: z.string().length(3),
});

const CartItemAddedPropertiesSchema = BaseEventPropertiesSchema.extend({
  product_id: z.string().uuid(),
  design_id: z.string().uuid().optional(),
  price: z.number().nonnegative(),
  currency: z.string().length(3),
  product_type: z.string().optional(),
  source: z.string().optional(),
});

const CartItemRemovedPropertiesSchema = BaseEventPropertiesSchema.extend({
  product_id: z.string().uuid(),
  reason: z.enum(['user_removed', 'out_of_stock', 'expired', 'other']).optional(),
  price: z.number().nonnegative().optional(),
});

const CartItemUpdatedPropertiesSchema = BaseEventPropertiesSchema.extend({
  product_id: z.string().uuid(),
  old_quantity: z.number().int().positive(),
  new_quantity: z.number().int().positive(),
  price: z.number().nonnegative().optional(),
  currency: z.string().length(3).optional(),
});

const CheckoutStartedPropertiesSchema = BaseEventPropertiesSchema.extend({
  cart_total: z.number().nonnegative(),
  item_count: z.number().int().positive(),
  currency: z.string().length(3),
  is_authenticated: z.boolean().optional(),
});

const CheckoutStepCompletedPropertiesSchema = BaseEventPropertiesSchema.extend({
  step_name: z.enum(['shipping', 'payment', 'review']),
  time_on_step_ms: z.number().int().nonnegative().optional(),
  cart_total: z.number().nonnegative().optional(),
  payment_method: z.string().optional(),
  express_checkout_type: z.enum(['apple_pay', 'google_pay']).optional(),
});

const PurchaseCompletedPropertiesSchema = BaseEventPropertiesSchema.extend({
  order_id: z.string().uuid().optional(),
  total: z.number().nonnegative().optional(),
  payment_method: z.string().min(1),
  currency: z.string().length(3).optional(),
  item_count: z.number().int().positive().optional(),
  has_add_ons: z.boolean().optional(),
  discount_code: z.string().optional(),
  discount_amount: z.number().nonnegative().optional(),
  express_checkout_type: z.enum(['apple_pay', 'google_pay']).optional(),
});

const PurchaseFailedPropertiesSchema = BaseEventPropertiesSchema.extend({
  error_message: z.string().min(1),
  step: z.string().min(1),
  cart_total: z.number().nonnegative().optional(),
  payment_method: z.string().optional(),
  express_checkout_type: z.enum(['apple_pay', 'google_pay']).optional(),
});

// =============================================================================
// ENGAGEMENT EVENT SCHEMAS
// =============================================================================

const CTAClickedPropertiesSchema = BaseEventPropertiesSchema.extend({
  cta_name: z.string().min(1),
  cta_location: z.string().min(1),
  cta_text: z.string().optional(),
  // FIX: Changed from z.string().url() to z.string() to accept relative paths like '/workshop'
  // The url() validator requires full URLs (http://...), but we often track internal navigation
  destination_url: z.string().optional(),
});

const InspirationViewedPropertiesSchema = BaseEventPropertiesSchema.extend({
  gallery_position: z.number().int().positive().optional(),
  gallery_size: z.number().int().positive().optional(),
  trigger: z.string().optional(),
});

const InspirationItemClickedPropertiesSchema = BaseEventPropertiesSchema.extend({
  item_index: z.number().int().nonnegative(),
  item_type: z.string().optional(),
  item_caption: z.string().optional(),
});

const SurpriseMeClickedPropertiesSchema = BaseEventPropertiesSchema.extend({
  selected_prompt: z.string().min(1),
  prompt_index: z.number().int().nonnegative().optional(),
});

const DesignSharedPropertiesSchema = BaseEventPropertiesSchema.extend({
  platform: z.enum(['facebook', 'twitter', 'pinterest', 'email', 'copy_link', 'other']),
  design_id: z.string().uuid().optional(),
});

const EmailCapturedPropertiesSchema = BaseEventPropertiesSchema.extend({
  source: z.enum(['exit_intent', 'footer', 'checkout', 'popup', 'other']),
  marketing_opt_in: z.boolean().optional(),
});

const CrossSellViewedPropertiesSchema = BaseEventPropertiesSchema.extend({
  products_shown: z.number().int().positive(),
  product_ids: z.array(z.string().uuid()),
});

const CrossSellAddedPropertiesSchema = BaseEventPropertiesSchema.extend({
  product_id: z.string().uuid(),
  product_name: z.string().min(1),
  price: z.number().int().nonnegative(),
  currency: z.string().length(3),
});

const AddOnsViewedPropertiesSchema = BaseEventPropertiesSchema.extend({
  addons_available: z.number().int().nonnegative(),
});

const AddOnChangedPropertiesSchema = BaseEventPropertiesSchema.extend({
  addon_id: z.string().min(1),
  addon_name: z.string().min(1),
  addon_price: z.number().int().nonnegative(),
});

const ContinueDesignShownPropertiesSchema = BaseEventPropertiesSchema.extend({
  sessions_count: z.number().int().positive(),
  oldest_session_age_hours: z.number().int().nonnegative().optional(),
});

const ContinueDesignClickedPropertiesSchema = BaseEventPropertiesSchema.extend({
  session_id: z.string().min(1),
  input_method: z.string().min(1),
  session_age_hours: z.number().int().nonnegative().optional(),
});

const ContinueDesignDismissedPropertiesSchema = BaseEventPropertiesSchema.extend({
  sessions_count: z.number().int().positive(),
});

// =============================================================================
// DROP-OFF EVENT SCHEMAS
// =============================================================================

const DesignAbandonedPropertiesSchema = BaseEventPropertiesSchema.extend({
  step_abandoned: z.string().min(1),
  time_spent_ms: z.number().int().nonnegative(),
  input_method: z.string().optional(),
  generation_in_progress: z.boolean().optional(),
});

const CartAbandonedPropertiesSchema = BaseEventPropertiesSchema.extend({
  cart_total: z.number().nonnegative(),
  item_count: z.number().int().positive(),
  time_in_cart_ms: z.number().int().nonnegative(),
  currency: z.string().length(3),
});

const CheckoutAbandonedPropertiesSchema = BaseEventPropertiesSchema.extend({
  step: z.enum(['shipping', 'payment', 'review']),
  cart_total: z.number().nonnegative(),
  time_in_checkout_ms: z.number().int().nonnegative().optional(),
  currency: z.string().length(3),
});

const SessionExpiredPropertiesSchema = BaseEventPropertiesSchema.extend({
  time_since_last_activity_ms: z.number().int().nonnegative(),
});

// =============================================================================
// SCHEMA MAP - Maps event names to their schemas
// =============================================================================

/**
 * Maps funnel event names to their Zod schemas.
 */
const FunnelEventSchemas: Record<FunnelEventName, z.ZodSchema> = {
  [FUNNEL_EVENTS.LANDING_VIEWED]: LandingViewedPropertiesSchema,
  [FUNNEL_EVENTS.WORKSHOP_STARTED]: WorkshopStartedPropertiesSchema,
  [FUNNEL_EVENTS.INPUT_METHOD_SELECTED]: InputMethodSelectedPropertiesSchema,
  [FUNNEL_EVENTS.GENERATION_STARTED]: GenerationStartedPropertiesSchema,
  [FUNNEL_EVENTS.GENERATION_COMPLETED]: GenerationCompletedPropertiesSchema,
  [FUNNEL_EVENTS.GENERATION_FAILED]: GenerationFailedPropertiesSchema,
  [FUNNEL_EVENTS.VARIANT_SELECTED]: VariantSelectedPropertiesSchema,
  [FUNNEL_EVENTS.PRODUCT_CONFIGURED]: ProductConfiguredPropertiesSchema,
  [FUNNEL_EVENTS.CART_VIEWED]: CartViewedPropertiesSchema,
  [FUNNEL_EVENTS.CART_ITEM_ADDED]: CartItemAddedPropertiesSchema,
  [FUNNEL_EVENTS.CART_ITEM_REMOVED]: CartItemRemovedPropertiesSchema,
  [FUNNEL_EVENTS.CART_ITEM_UPDATED]: CartItemUpdatedPropertiesSchema,
  [FUNNEL_EVENTS.CHECKOUT_STARTED]: CheckoutStartedPropertiesSchema,
  [FUNNEL_EVENTS.CHECKOUT_STEP_COMPLETED]: CheckoutStepCompletedPropertiesSchema,
  [FUNNEL_EVENTS.PURCHASE_COMPLETED]: PurchaseCompletedPropertiesSchema,
  [FUNNEL_EVENTS.PURCHASE_FAILED]: PurchaseFailedPropertiesSchema,
};

/**
 * Maps engagement event names to their Zod schemas.
 */
const EngagementEventSchemas: Record<EngagementEventName, z.ZodSchema> = {
  [ENGAGEMENT_EVENTS.CTA_CLICKED]: CTAClickedPropertiesSchema,
  [ENGAGEMENT_EVENTS.INSPIRATION_VIEWED]: InspirationViewedPropertiesSchema,
  [ENGAGEMENT_EVENTS.INSPIRATION_ITEM_CLICKED]: InspirationItemClickedPropertiesSchema,
  [ENGAGEMENT_EVENTS.SURPRISE_ME_CLICKED]: SurpriseMeClickedPropertiesSchema,
  [ENGAGEMENT_EVENTS.DESIGN_SHARED]: DesignSharedPropertiesSchema,
  [ENGAGEMENT_EVENTS.EMAIL_CAPTURED]: EmailCapturedPropertiesSchema,
  [ENGAGEMENT_EVENTS.EMAIL_MODAL_SHOWN]: BaseEventPropertiesSchema,
  [ENGAGEMENT_EVENTS.EMAIL_MODAL_DISMISSED]: BaseEventPropertiesSchema,
  [ENGAGEMENT_EVENTS.FEATURE_DISCOVERED]: BaseEventPropertiesSchema.extend({
    feature_name: z.string().min(1),
  }),
  [ENGAGEMENT_EVENTS.CROSS_SELL_VIEWED]: CrossSellViewedPropertiesSchema,
  [ENGAGEMENT_EVENTS.CROSS_SELL_ADDED]: CrossSellAddedPropertiesSchema,
  [ENGAGEMENT_EVENTS.ADDONS_VIEWED]: AddOnsViewedPropertiesSchema,
  [ENGAGEMENT_EVENTS.ADDON_ADDED]: AddOnChangedPropertiesSchema,
  [ENGAGEMENT_EVENTS.ADDON_REMOVED]: AddOnChangedPropertiesSchema,
  [ENGAGEMENT_EVENTS.CONTINUE_DESIGN_SHOWN]: ContinueDesignShownPropertiesSchema,
  [ENGAGEMENT_EVENTS.CONTINUE_DESIGN_CLICKED]: ContinueDesignClickedPropertiesSchema,
  [ENGAGEMENT_EVENTS.CONTINUE_DESIGN_DISMISSED]: ContinueDesignDismissedPropertiesSchema,
};

/**
 * Maps drop-off event names to their Zod schemas.
 */
const DropOffEventSchemas: Record<DropOffEventName, z.ZodSchema> = {
  [DROP_OFF_EVENTS.DESIGN_ABANDONED]: DesignAbandonedPropertiesSchema,
  [DROP_OFF_EVENTS.CART_ABANDONED]: CartAbandonedPropertiesSchema,
  [DROP_OFF_EVENTS.CHECKOUT_ABANDONED]: CheckoutAbandonedPropertiesSchema,
  [DROP_OFF_EVENTS.SESSION_EXPIRED]: SessionExpiredPropertiesSchema,
};

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate event properties against their schema.
 * Returns validated properties or logs errors (non-blocking).
 *
 * @param eventName - Event name
 * @param properties - Event properties to validate
 * @param eventType - Type of event (funnel, engagement, drop-off)
 * @returns Validated properties or original properties if validation fails
 */
export function validateEventProperties(
  eventName: string,
  properties: Record<string, unknown>,
  eventType: 'funnel' | 'engagement' | 'drop-off'
): Record<string, unknown> {
  let schema: z.ZodSchema | undefined;

  // Get schema based on event type (check key existence to handle unknown event names)
  if (eventType === 'funnel' && eventName in FunnelEventSchemas) {
    schema = FunnelEventSchemas[eventName as FunnelEventName];
  } else if (eventType === 'engagement' && eventName in EngagementEventSchemas) {
    schema = EngagementEventSchemas[eventName as EngagementEventName];
  } else if (eventType === 'drop-off' && eventName in DropOffEventSchemas) {
    schema = DropOffEventSchemas[eventName as DropOffEventName];
  }

  // If no schema found (unknown event name), log warning but don't block
  if (!schema) {
    if (import.meta.env.DEV) {
      logger.warn(`[Analytics] No schema found for event: ${eventName} (${eventType})`, {
        component: 'analytics.schemas',
        action: 'schema_validation',
        eventName,
        eventType,
      });
    }
    return properties;
  }

  // Validate properties
  const result = schema.safeParse(properties);

  if (result.success) {
    return result.data as Record<string, unknown>;
  }

  // Log validation errors (non-blocking)
  if (import.meta.env.DEV) {
    logger.error(
      `[Analytics] Event validation failed for ${eventName}`,
      new Error('Schema validation failed'),
      {
        component: 'analytics.schemas',
        action: 'schema_validation',
        eventName,
        eventType,
        validationErrors: result.error.issues,
        originalProperties: properties,
      }
    );
  }

  // Return original properties (don't break user experience)
  // In production, you might want to send to error tracking service
  return properties;
}
